import React, { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'
import { Uploader } from 'plupload'

import { API_ENDPOINT, ATTACHMENT_NUMBER_LIMIT } from '../../../../../../../../../../config'
import { finishUpload } from '../../../../../../../../../../redux/thunks/multimedia/multimedia'
import { useAppDispatch, useAppSelector } from '../../../../../../../../../../app/hooks'

import { UploadFile } from '../../../../../../../../../../@types/multimedia-types'
import { AttachmentType } from '../../../../../../../../../../@types/types'
import { showMultimediaMessage } from '../../../../../../../../../../redux/reducers/message/messageFeature'
import { trackingOnAttachmentUpload } from '../../../../../../../../../../tracking/tracking'

import {
  progressUploadAttachment,
  startUploadAttachmentRequest,
} from '../../../../../../../../../../redux/reducers/multimedia/multimediaFeature'
import MultimediaButtonContent from '../common/MultimediaButtonContent'
import { getMultimediaButtonContent, MultimediaButtonTypes } from '../common/mediaButtonContentProvider'
import { WebViewMultimediaButton } from './WebViewMultimediaButton'
import { MandatoryHintWrapper } from '../../../../../../../../../../components/common/display/mandatoryHint/MandatoryHintWrapper'

import styles from '../common/MultimediaButtonCommon.module.css'
import { InitState } from '../../../../../../../../../../redux/reducers/root/initialState'
import { filenameIsValid } from '../../../../../../../../../../services/validation/rules/attachmentFileNameRule'
import { isWebView } from '../../../../../../../../../../utils/navigation'
import { Button } from 'is24-corecss'

export const MultimediaButton: React.FC<MultimediaButtonTypes> = (props) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const pluploadButtonRef = useRef<HTMLDivElement>(null)
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const dropzoneId = uuid()
  const { fileTypes, title, Icon } = getMultimediaButtonContent(props.allowedFileType)
  const attachmentsNumber = useAppSelector((state: InitState) => state.multimediaFeature.attachments?.length || 0)
  const isMobile = isWebView() && props.flowInformation.isMobileFlow

  useEffect(() => {
    const uploader: Uploader = new Uploader({
      runtimes: 'html5,html4',
      browse_button: isMobile ? pluploadButtonRef.current : buttonRef.current,
      url: API_ENDPOINT + '/anbieten/private-anbieter/inserieren/api/multimedia/upload',
      file_data_name: 'file',
      init: {
        FilesAdded: (up, files) => {
          if (files?.length + attachmentsNumber >= ATTACHMENT_NUMBER_LIMIT) {
            files.forEach((file) => up.removeFile(file))
            dispatch(showMultimediaMessage({ message: 'attachmentsNumber' }))
            return
          }
          const invalidFileName = files.some((file) => !filenameIsValid(file.name))
          if (invalidFileName) {
            dispatch(showMultimediaMessage({ message: 'attachmentFilename' }))
            files.forEach((file) => {
              up.removeFile(file)
            })
            return
          }

          files.forEach((file: UploadFile) => {
            const attachmentType = file.type?.includes('image') ? AttachmentType.PICTURE : AttachmentType.DOCUMENT
            dispatch(startUploadAttachmentRequest({ attachmentType: attachmentType, file: file }))
          })
          trackingOnAttachmentUpload(props.flowInformation, props.allowedFileType, props.entryMode)
          up.start()
          up.refresh()
        },
        UploadProgress: (up, file) => {
          dispatch(progressUploadAttachment({ fileId: file.id, percent: file.percent }))
        },
        PostInit: () => uploader.refresh(),
        FileUploaded: (up, file, response) => {
          dispatch(finishUpload(file, response))
        },
        Error: (_, error) => {
          if (error.code === -600) {
            dispatch(
              showMultimediaMessage({
                message: 'attachmentFileSize',
                values: {
                  filename: error.file.name,
                },
              })
            )
          } else if (error.code === -601) {
            dispatch(
              showMultimediaMessage({
                message: 'attachmentFileType',
                values: {
                  filename: error.file.name,
                },
              })
            )
          } else {
            dispatch(showMultimediaMessage({ message: 'attachment' }))
          }
        },
      },
      multi_selection: true,
      drop_element: dropzoneId,
      filters: {
        max_file_size: '50mb',
        mime_types: [
          { extensions: props.allowedFileType, title: t('wizard.screen.multimedia.supportedFiles', 'Supported Files') },
        ],
      },
      multipart_params: {
        isFloorPlan: props.isFloorPlan,
      },
    })
    uploader.init()
    uploader.refresh()

    // Store uploader reference globally for dynamic updates
    ;(window as any).pluploadInstance = uploader

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return isMobile && props.flowInformation.isMobileFlow ? (
    <div className={'margin-bottom-l'}>
      <WebViewMultimediaButton ref={pluploadButtonRef} dropzoneId={dropzoneId} />
      <MandatoryHintWrapper
        showMandatoryHint={true}
        iconClassName={'font-lightgray font-body-small-regular float-left margin-bottom-l'}
      />
    </div>
  ) : (
    <Button
      id={dropzoneId}
      ref={buttonRef}
      className={`${styles.button} cursor-pointer align-left border-radius-rounded`}
    >
      <MultimediaButtonContent Icon={Icon} title={title} subtitle={fileTypes} />
    </Button>
  )
}
